import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import type { FilterCacheFindInterface } from '@zaoblako/shared/modules/personalization/dtos/filter-cache/filter-cache.find.dto';
import type { FilterDigestFindInterface } from '@zaoblako/shared/modules/personalization/dtos/filter-digest/filter-digest-find.dto';
import { FilterCacheKey } from '@zaoblako/shared/modules/personalization/enums/filter-cache-key.enum';
import { ReportTypeEnum } from '@zaoblako/shared/modules/personalization/enums/report.type.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { FilterCacheService } from '@zaoblako/web/core/services/personalization/filter-cache.service';
import { FilterDigestService } from '@zaoblako/web/core/services/personalization/filter-digest.service';
import { flatten, unflatten } from 'flat';
import { isNil } from 'lodash';
import { forkJoin, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

const paramsMap = {
  clientIds: 'clients:array:id',
  directionIds: 'directions:hierarchy:id',
  from: 'from:string',
  limit: 'limit:number',
  stepping: 'stepping:boolean',
  offset: 'offset:number',
  responsibleIds: 'responsible:array:id',
  search: 'search:string',
  sort: 'sort:string',
  squareMax: 'squareMax:number',
  squareMin: 'squareMin:number',
  statusIds: 'statuses:array:id',
  groupIds: 'groups:array:id',
  tagIds: 'tags:array:id',
  to: 'to:string',
  types: 'types:array',
  statuses: 'statuses:array',
  year: 'year:string',
  userIds: 'users:array:id',
  taskStatusIds: 'taskStatuses:array:id',
  taskTypeIds: 'taskTypes:array:id',
  budgetStatusIds: 'budgetStatuses:array:id',
  brsTaskStatusIds: 'brsTaskStatuses:array:id',
  operatorIds: 'operators:array:id',
  bankIds: 'banks:array:id',
  formIds: 'forms:array:id',
  filialIds: 'filials:array:id',
  unitIds: 'units:array:id',
  contragentIds: 'contragents:array:id',
  companyIds: 'companies:array:id',
  documentTypeIds: 'documentTypes:array:id',
  method: 'methods:array',
  employeeIds: 'employees:array:id',
  masterMilestoneIds: 'masterMilestones:array:id',
  creatorIds: 'creators:array:id',
  dealIds: 'deals:array:id',
  projectIds: 'projects:array:id',
  helpdeskTaskIds: 'helpdeskTasks:array:id',
  ndsIds: 'nds:array:id',
  vatIds: 'vats:array:id',
  operationTypeIds: 'operationTypes:array:id',
  documentTypeCodes: 'documentTypes:array:code',
  providerIds: 'providers:array:id',
  categoryIds: 'categories:array:id',
  planNameIds: 'planNames:array:id',
  brandIds: 'brands:array:id',
  rangeIds: 'ranges:array:id',
  priorityIds: 'priorities:array:id',
  templateIds: 'templates:array:id',
  bankDetailsIds: 'bankDetails:array:id',
  contractIds: 'contracts:array:id',
  roomIds: 'rooms:array:id',
  connectionIds: 'connections:array:id',
  dealTypeIds: 'dealTypes:array:id'
};

function isDefinedQueryParamValue(value: unknown): boolean {
  if (value === false || value === 0) {
    return true;
  }

  return value !== undefined && value !== null && value !== '';
}

function hasRecordKey(source: unknown, key: string): boolean {
  return typeof source === 'object' && source !== null && Object.prototype.hasOwnProperty.call(source, key);
}

/** FilterCache / digest: yup rejects `null` entries in `select[]`. */
function compactStringArray(values: unknown[] | undefined): string[] | undefined {
  if (!Array.isArray(values)) {
    return undefined;
  }

  const compact = values.filter((value): value is string => typeof value === 'string' && value.length > 0);
  return compact.length ? compact : undefined;
}

@Injectable({
  providedIn: 'root'
})
export class QueryParamsService {
  public constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly globalService: GlobalService,
    private readonly filterCacheService: FilterCacheService,
    private readonly filterDigestService: FilterDigestService
  ) {}

  public getFilteredQueryParams<T>(
    dto: SerdeDtoClass<T>,
    params: {
      required?: Partial<Record<keyof T, any>>;
      optional?: Partial<Record<keyof T, any>>;
      filterType?: ReportTypeEnum;
      filterCacheType?: FilterCacheKey;
      sessionStorageName?: string;
    }
  ): Observable<{ filters: T; select?: string[] }> {
    const result: T = Object.fromEntries(Object.keys(dto.getMetadata()).map((key) => [key, undefined])) as T;
    const queryParams = unflatten(this.route.snapshot.queryParams);
    const filterTypeStorage: Record<keyof T, any> = this.globalService.globalInfo.savedFilters?.find(
      (savedFilter) => savedFilter.report === params?.filterType && savedFilter.isFavorite
    )?.filter;
    const redirectStorage: Record<keyof T, any> = JSON.parse(
      sessionStorage.getItem(`${params.sessionStorageName}.redirect.queryParams`)
    );

    const parseParams = (filterCache?: FilterCacheFindInterface<T>): T => {
      Object.keys(result).forEach((key) => {
        if (params?.required?.hasOwnProperty(key)) {
          result[key] = params.required[key];
        }

        if (redirectStorage) {
          if (key === 'offset') {
            result[key] = 0;
            return;
          }

          if (key === 'search') {
            delete result[key];
            return;
          }

          if (hasRecordKey(redirectStorage, key)) {
            result[key] = redirectStorage[key];
          }
          return;
        }

        if (Object.keys(queryParams)?.length) {
          if (key === 'offset') {
            result[key] = 0;
            return;
          }

          if (key === 'search') {
            delete result[key];
            return;
          }

          const type = paramsMap[key] && paramsMap[key].includes(':') ? paramsMap[key].split(':')[1] : '';

          if (isDefinedQueryParamValue(queryParams[key])) {
            result[key] = queryParams[key];

            if (type === 'number') {
              result[key] = +result[key];
            } else if (type === 'boolean' || ['true', 'false'].includes(String(result[key]).toLowerCase())) {
              result[key] = String(result[key]).toLowerCase() === 'true';
            }
          }
        } else {
          if (result[key] === undefined && params?.optional?.[key] !== undefined && params?.optional?.[key] !== null) {
            result[key] = params.optional[key];
          }
          if (filterTypeStorage) {
            if (hasRecordKey(filterTypeStorage, key)) {
              result[key] = filterTypeStorage[key];
            }
          } else if (filterCache?.filters) {
            if (hasRecordKey(filterCache.filters, key)) {
              result[key] = filterCache.filters[key];
            }
          }
        }

        if (
          isNil(result[key]) ||
          Number.isNaN(result[key]) ||
          (result[key]?.length === 1 && result[key][0] === undefined) ||
          result[key] === '[object Object]' ||
          result[key] === '{}' ||
          (typeof result[key] === 'object' && !Object.keys(result[key]).length)
        ) {
          delete result[key];
        }
      });

      if (redirectStorage) {
        sessionStorage.removeItem(`${params.sessionStorageName}.redirect.queryParams`);
      }

      return result;
    };

    if (params.filterCacheType) {
      return this.filterCacheService
        .findOneByKey<T>(params.filterCacheType)
        .pipe(map((filterCache) => ({ filters: parseParams(filterCache), select: filterCache.select })));
    } else {
      return of({ filters: parseParams() });
    }
  }

  public setFilterCache<T>(filterCacheType: FilterCacheKey, filters: T, select?: string[]) {
    this.filterCacheService.save(filterCacheType, { filters, select }).subscribe();
  }

  public setQueryParams(
    queryParams: Record<string, any>,
    params: {
      isNotSave?: boolean;
      filterCacheType?: FilterCacheKey;
      select?: string[];
      noScrollingToTop?: boolean;
      notSaveFields?: string[];
      notDeleteSort?: boolean;
    }
  ) {
    const filteredParams = flatten({ ...queryParams }) as Record<string, any>;

    delete filteredParams.limit;
    delete filteredParams.offset;
    delete filteredParams.search;
    delete filteredParams.filtersCount;

    if (!params.notDeleteSort) {
      delete filteredParams.sort;
    }

    Object.keys(filteredParams).forEach((key) => {
      if (
        isNil(filteredParams[key]) ||
        Number.isNaN(filteredParams[key]) ||
        (filteredParams[key]?.length === 1 && filteredParams[key][0] === undefined) ||
        filteredParams[key] === '[object Object]' ||
        filteredParams[key] === '{}' ||
        (typeof filteredParams[key] === 'object' && !Object.keys(filteredParams[key]).length)
      ) {
        delete filteredParams[key];
      }
    });

    if (params?.filterCacheType && !params?.isNotSave) {
      if (params?.notSaveFields?.length) {
        const savedFilter = { ...queryParams };
        Object.keys(savedFilter).forEach((key) => {
          if (params?.notSaveFields?.includes(key)) {
            delete savedFilter[key];
          }
        });
        this.setFilterCache(params?.filterCacheType, savedFilter, params?.select);
      } else {
        this.setFilterCache(params?.filterCacheType, queryParams, params?.select);
      }
    }

    this.replaceQueryParams(filteredParams, !!params?.noScrollingToTop);
  }

  public getFilteredQueryParamsV2<T>(
    dto: SerdeDtoClass<T>,
    params: {
      required?: Partial<Record<keyof T, any>>;
      optional?: Partial<Record<keyof T, any>>;
      filterType?: ReportTypeEnum;
      filterCacheType?: FilterCacheKey;
      sessionStorageName?: string;
    }
  ): Observable<{ filters: T; select?: string[]; persistedFilterKeys: ReadonlySet<string>; }> {
    const result: T = Object.fromEntries(Object.keys(dto.getMetadata()).map((key) => [key, undefined])) as T;
    const persistedFilterKeys = new Set<string>();
    const filterTypeStorage: Record<keyof T, any> = this.globalService.globalInfo.savedFilters?.find(
      (savedFilter) => savedFilter.report === params?.filterType && savedFilter.isFavorite
    )?.filter;
    const redirectStorage: Record<keyof T, any> = JSON.parse(
      sessionStorage.getItem(`${params.sessionStorageName}.redirect.queryParams`)
    );
    const digest = unflatten(this.route.snapshot.queryParams)?.['digest'];
    const queryParams = unflatten(this.route.snapshot.queryParams);
    let filterParams = {};

    const markPersisted = (key: string): void => {
      persistedFilterKeys.add(key);
    };

    const parseParams = (filterCache?: FilterCacheFindInterface<T>): T => {
      Object.keys(result).forEach((key) => {
        if (params?.required?.hasOwnProperty(key)) {
          result[key] = params.required[key];
        }

        if (key === 'search') {
          delete result[key];
          return;
        }

        if (redirectStorage) {
          if (key === 'offset') {
            result[key] = 0;
            return;
          }

          if (hasRecordKey(redirectStorage, key)) {
            result[key] = redirectStorage[key];
            markPersisted(key);
          }
          return;
        }

        if (filterParams && Object.keys(filterParams)?.length) {
          if (key === 'offset') {
            result[key] = 0;
            return;
          }

          const type = paramsMap[key] && paramsMap[key].includes(':') ? paramsMap[key].split(':')[1] : '';

          if (isDefinedQueryParamValue(filterParams[key])) {
            result[key] = filterParams[key];
            markPersisted(key);

            if (type === 'number') {
              result[key] = +result[key];
            } else if (type === 'boolean' || ['true', 'false'].includes(String(result[key]).toLowerCase())) {
              result[key] = String(result[key]).toLowerCase() === 'true';
            }
          }
        } else {
          if (result[key] === undefined && params?.optional?.[key] !== undefined && params?.optional?.[key] !== null) {
            result[key] = params.optional[key];
          }
          if (filterTypeStorage) {
            if (hasRecordKey(filterTypeStorage, key)) {
              result[key] = filterTypeStorage[key];
              markPersisted(key);
            }
          } else if (filterCache?.filters) {
            if (hasRecordKey(filterCache.filters, key)) {
              result[key] = filterCache.filters[key];
              markPersisted(key);
            }
          }
        }

        if (
          isNil(result[key]) ||
          Number.isNaN(result[key]) ||
          result[key] === '[object Object]' ||
          result[key] === '{}' ||
          (typeof result[key] === 'object' && !Object.keys(result[key]).length) ||
          (Array.isArray(result[key]) && result[key].every((el) => [undefined, null].includes(el)))
        ) {
          delete result[key];
        }
      });

      if (redirectStorage) {
        sessionStorage.removeItem(`${params.sessionStorageName}.redirect.queryParams`);
      }

      return result;
    };

    if (params.filterCacheType && !digest) {
      return this.filterCacheService.findOneByKey<T>(params.filterCacheType).pipe(
        switchMap((filterCache: FilterCacheFindInterface<T>) => {
          if (
            Object.keys(queryParams).filter((key) => !['digest', 'limit', 'offset', 'isStartSearch'].includes(key))
              ?.length
          ) {
            filterParams = queryParams;
          }
          const parseFilter = parseParams(filterCache);
          return this.filterDigestService
            .createOne({ filters: parseFilter, select: filterCache.select })
            .pipe(
              switchMap((data) =>
                this.updateUrlWithDigest(data.digest).pipe(
                  map(() => ({
                    filters: parseFilter,
                    select: filterCache.select,
                    persistedFilterKeys
                  }))
                )
              )
            );
        })
      );
    } else if (params.filterCacheType && digest) {
      return this.filterDigestService.findOne<T>(digest).pipe(
        tap((filterDigest) => {
          filterParams = filterDigest.filter?.['filters'];
        }),
        switchMap((filterDigest: FilterDigestFindInterface<T>) => {
          const parseFilter = parseParams({ ...filterDigest.filter } as any);
          return this.filterDigestService
            .createOne({
              filters: parseFilter,
              select: (filterDigest.filter as any)?.select
            })
            .pipe(
              switchMap((data) =>
                this.updateUrlWithDigest(data.digest).pipe(
                  map(() => ({
                    filters: parseFilter,
                    select: (filterDigest.filter as any)?.select,
                    persistedFilterKeys
                  }))
                )
              )
            );
        })
      );
    } else if (!params.filterCacheType) {
      return of({ filters: parseParams(), persistedFilterKeys });
    }
  }

  public getUrlTreeWithDigest(
    commands: string[],
    queryParams: Record<string, any>,
    isStartSearch: boolean = false
  ): Observable<UrlTree> {
    let payloadSelect$: Observable<FilterCacheFindInterface>;

    if (commands[0] === '/document/list') {
      payloadSelect$ = this.filterCacheService.findOneByKey(FilterCacheKey.DOCUMENTS);
    }

    return (payloadSelect$ ?? of(null)).pipe(
      switchMap((filterCache: FilterCacheFindInterface | null) => {
        if (filterCache) {
          return this.filterDigestService.createOne({
            filters: {
              ...queryParams,
              select:
                filterCache.filters?.select && Array.isArray(filterCache.filters?.select)
                  ? filterCache.filters?.select
                  : filterCache.select
            }
          });
        }
        return this.filterDigestService.createOne({ filters: queryParams });
      }),
      map((data: FilterDigestFindInterface) => {
        return this.router.createUrlTree(commands, {
          queryParams: flatten({
            digest: data.digest,
            isStartSearch: isStartSearch
          })
        });
      })
    );
  }

  private updateUrlWithDigest(digest: string) {
    return from(
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { digest },
        replaceUrl: true
      })
    ).pipe(
      catchError((error) => {
        console.error('Navigation failed:', error);
        return of(false);
      })
    );
  }

  public setQueryParamsV2(
    queryParams: Record<string, any>,
    params: {
      isNotSave?: boolean;
      filterCacheType?: FilterCacheKey;
      select?: string[];
      noScrollingToTop?: boolean;
      notSaveFields?: string[];
      notDeleteSort?: boolean;
    }
  ) {
    const filteredParams = flatten({ ...queryParams }) as Record<string, any>;
    const digest = unflatten(this.route.snapshot.queryParams)['digest'];
    const isStartSearch = unflatten(this.route.snapshot.queryParams)?.['isStartSearch'] === 'true';

    delete filteredParams.limit;
    delete filteredParams.offset;
    delete filteredParams.search;
    delete filteredParams.filtersCount;

    if (!params.notDeleteSort) {
      delete filteredParams.sort;
    }

    Object.keys(filteredParams).forEach((key) => {
      if (
        isNil(filteredParams[key]) ||
        Number.isNaN(filteredParams[key]) ||
        (filteredParams[key]?.length === 1 && filteredParams[key][0] === undefined) ||
        filteredParams[key] === '[object Object]' ||
        filteredParams[key] === '{}' ||
        (typeof filteredParams[key] === 'object' && !Object.keys(filteredParams[key]).length)
      ) {
        delete filteredParams[key];
      }
    });

    if (params?.filterCacheType && !params?.isNotSave) {
      const savedFilter = { ...queryParams };
      Object.keys(savedFilter).forEach((key) => {
        if (params?.notSaveFields?.includes(key)) {
          delete savedFilter[key];
        }
      });
      const filtersSource = params?.notSaveFields?.length ? savedFilter : queryParams;
      const filters = { ...filtersSource };
      delete filters.select;
      const select = compactStringArray(params?.select ?? (filtersSource.select as string[] | undefined));
      const digestObs = digest
        ? this.filterDigestService.updateOne(digest, {
            filters,
            select
          })
        : of([]);
      const defaultObs = !isStartSearch
        ? this.filterCacheService.save(params?.filterCacheType, {
            filters,
            select
          })
        : of([]);
      const resObs = [defaultObs, digestObs];

      forkJoin(resObs).subscribe();
    }

    this.replaceQueryParams(digest ? { digest } : null, !!params?.noScrollingToTop);
  }

  /**
   * replaceUrl-навигация с query. При `preserveScroll` не сбрасывает window scroll
   * (и пропускает navigate, если URL уже совпадает).
   */
  private replaceQueryParams(queryParams: Record<string, unknown> | null, preserveScroll: boolean): void {
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams
    });

    if (
      this.router.isActive(urlTree, {
        paths: 'exact',
        queryParams: 'exact',
        fragment: 'ignored',
        matrixParams: 'ignored'
      })
    ) {
      return;
    }

    const scrollY = window.scrollY;
    this.router
      .navigateByUrl(urlTree, { replaceUrl: true })
      .then(() => {
        if (preserveScroll) {
          this.restoreWindowScroll(scrollY);
        }
      })
      .catch(console.error);
  }

  private restoreWindowScroll(scrollY: number): void {
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY });
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY });
      });
    });
  }

  public getFilteredEntities<T extends object>(filters: T, queryParams: Record<string, any>, dedupFieldNames?: boolean): T {
    const result = {} as T;

    const alreadyFilled = new Set<string>();
    Object.keys(paramsMap).forEach((key) => {
      const [fieldName, fieldType, fieldKey] = paramsMap[key].split(':');

      if (!(fieldName in filters)) {
        return;
      }
      if (dedupFieldNames && alreadyFilled.has(fieldName)) {
        return;
      }
      alreadyFilled.add(fieldName);

      if (fieldType === 'array') {
        if (!isNil(queryParams[key]) && !Array.isArray(queryParams[key])) {
          queryParams[key] = [queryParams[key]];
        }

        result[fieldName] =
          queryParams[key] && fieldKey
            ? filters[fieldName].filter((option) =>
                queryParams[key].map((param) => String(param)).includes(String(option[fieldKey]))
              )
            : queryParams[key] || [];
      } else if (fieldType === 'hierarchy') {
        if (!isNil(queryParams[key]) && !Array.isArray(queryParams[key])) {
          queryParams[key] = [queryParams[key]];
        }

        if (key === 'directionIds') {
          let listHierarchy = [];
          const parseChildren = (_options: any[]) => {
            _options.forEach((option) => {
              if (
                queryParams[key] &&
                queryParams[key].findIndex((item) => item?.toString() === String(option[fieldKey])) !== -1
              ) {
                listHierarchy.push(option);
              }
              if (option.children?.length) {
                parseChildren(option.children);
              }
            });
          };

          parseChildren(filters[fieldName]);
          result[fieldName] = listHierarchy;
        } else {
          let listHierarchy = [];
          const parseChildren = (_options: any[], parent?: any) => {
            _options.forEach((option) => {
              if (
                queryParams[key] &&
                queryParams[key].findIndex((si) => si?.toString() === String(option[fieldKey])) !== -1
              ) {
                listHierarchy.push(option);
              }
              if (option.children?.length) {
                parseChildren(option.children, option);
              }
            });
            if (
              parent?.children?.every(
                (child) => listHierarchy.findIndex((selectItem) => selectItem[fieldKey] === child[fieldKey]) !== -1
              ) &&
              listHierarchy.findIndex((selectItem) => selectItem[fieldKey] === parent?.[fieldKey]) === -1
            ) {
              listHierarchy.push(parent);
            }
            if (
              !parent?.children?.every(
                (child) => listHierarchy.findIndex((selectItem) => selectItem[fieldKey] === child[fieldKey]) !== -1
              ) &&
              listHierarchy.findIndex((selectItem) => selectItem[fieldKey] === parent?.[fieldKey]) !== -1
            ) {
              listHierarchy = listHierarchy.filter((selectItem) => selectItem[fieldKey] !== parent?.[fieldKey]);
            }
          };

          parseChildren(filters[fieldName]);
          result[fieldName] = listHierarchy;
        }
      }
    });

    return { ...result };
  }
}
